import React, { useRef } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Tooltip from '@src/components/Tooltip';
import actions from '@src/redux/actions';
import { getExportItemState, getExportTypeLabel } from '@src/services/project';
import { COLOR } from '@src/styles/color';
import { delay } from '@src/utils/delay';
import { downloadFileByUrl, getDownloadFileName } from '@src/utils/file';
import useExportRetentionPeriod from '@src/hooks/useExportRetentionPeriod';
import ExportFileTypeIcon from './ExportFileTypeIcon';
import { StyledExportHistoryItem } from './index.style';

const CREATED_AT_FORMAT = 'HH:mm - DD/MM/YYYY';

// How long a transfer has to last before it is worth reporting, and how long
// the report then stays up. A file the browser has cached arrives in a few
// milliseconds, and a click on one must not flash the ring and the tooltip in
// and out. Both numbers sit under the delay a click still reads as immediate.
const REPORT_AFTER_MS = 200;
const REPORT_AT_LEAST_MS = 400;

const ExportHistoryItem = ({ item, audioType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const fallbackRetentionPeriod = useExportRetentionPeriod();

  const { isPending, isFailed, isExpired, canDownload, retentionDays } =
    getExportItemState(item, fallbackRetentionPeriod);
  const typeLabel = getExportTypeLabel(item, audioType);
  const fileName = getDownloadFileName(item?.fileName, item?.fileUrl);

  // A row is mounted only while the history tab is open, and the download it
  // starts outlives it: closing the tab does not stop the transfer. The store
  // is what a reopened row reads to know its file is still on the way.
  const isDownloading = useSelector((state) =>
    state.project.downloadingExportIds.includes(item?.id),
  );
  // The button stays clickable while its file is being fetched, so the second
  // click is turned away here. `isDownloading` cannot do it: it stays false for
  // the whole delay below, and even after that it is read from a render the
  // first click has not replaced yet.
  const downloadingRef = useRef(false);

  const handleDownload = async () => {
    // Downloads the stored file as it is: no new export, so no credit is spent,
    // and the popover stays open for downloading another file.
    if (isDownloading || downloadingRef.current) return;

    downloadingRef.current = true;
    const startedAt = Date.now();
    const reportTimer = setTimeout(
      () => dispatch(actions.project.setExportDownloading(item?.id, true)),
      REPORT_AFTER_MS,
    );

    try {
      await downloadFileByUrl(item?.fileUrl, fileName);
    } catch (error) {
      dispatch(
        actions.noti.push({ severity: 'error', message: 'downloadError' }),
      );
    } finally {
      clearTimeout(reportTimer);
      // A transfer that ends just past the delay above has already put the ring
      // on screen, so it is held until it has been up long enough to read.
      const reportedFor = Date.now() - startedAt - REPORT_AFTER_MS;
      if (reportedFor > 0 && reportedFor < REPORT_AT_LEAST_MS)
        await delay(REPORT_AT_LEAST_MS - reportedFor);
      downloadingRef.current = false;
      dispatch(actions.project.setExportDownloading(item?.id, false));
    }
  };

  const getStatus = () => {
    if (isPending) return { text: t('exportProcessing') };
    if (isFailed) return { text: t('exportFailed'), isError: true };
    if (!item?.createdAt) return null;
    return { text: moment(item.createdAt).format(CREATED_AT_FORMAT) };
  };

  const renderAction = () => {
    if (isPending)
      return (
        <div className="status-icon">
          <CircularProgress className="pending-icon" size={24} />
        </div>
      );

    if (isFailed)
      return (
        <div className="status-icon">
          <WarningRoundedIcon className="failed-icon" />
        </div>
      );

    const getDownloadTitle = () => {
      if (isExpired)
        return t('notiExportFileExpired', { retentionPeriod: retentionDays });
      // While the file is on its way the button answers no click, so naming the
      // action it no longer offers would be a promise it does not keep.
      if (isDownloading) return t('downloadingFile');
      return t('download');
    };

    return (
      <Tooltip title={getDownloadTitle()} arrow placement="top">
        {/* A disabled button fires no hover event, so the tooltip listens on
            the wrapper instead. */}
        <span className="download-wrap">
          <button
            type="button"
            className={
              isDownloading ? 'download-btn downloading' : 'download-btn'
            }
            onClick={handleDownload}
            disabled={!canDownload}
          >
            {/* `downloadFileByUrl` holds the whole file in memory before the
                browser is told a download exists, so the browser's own download
                UI stays empty for the length of the transfer. Until then this
                button is the only report that the click did anything. */}
            {isDownloading ? (
              // `flexShrink`: `.download-btn` leaves 22px of content box, and
              // the icon this stands in for carries `flex-shrink: 0`. Without
              // the same, the ring is squeezed to 22px and draws as an ellipse.
              // `color`: a ring with no colour is `primary.main`, which is
              // yellow here, and the icon it replaces is not.
              <CircularProgress
                size={24}
                sx={{ flexShrink: 0, color: COLOR.indigo[80] }}
              />
            ) : (
              <DownloadRoundedIcon />
            )}
          </button>
        </span>
      </Tooltip>
    );
  };

  const status = getStatus();

  return (
    <StyledExportHistoryItem>
      <div className="file-icon">
        <ExportFileTypeIcon type={item?.type} />
      </div>
      <div className="info">
        <p className="file-name" title={fileName}>
          {fileName}
        </p>
        <div className="details">
          {status && (
            <>
              <span
                className={
                  status.isError ? 'detail-text failed' : 'detail-text'
                }
              >
                {status.text}
              </span>
              <span className="dot" />
            </>
          )}
          <span className="detail-text">
            {typeLabel ? t(typeLabel.key, typeLabel.params) : item?.type}
          </span>
        </div>
      </div>
      {renderAction()}
    </StyledExportHistoryItem>
  );
};

export default ExportHistoryItem;
